import { useState } from "react";
import type { FormEvent } from "react";
import { Mail, Send, Server } from "lucide-react";

type SubmitState = "idle" | "submitting" | "success" | "error";

export default function RegisterSeller() {
  const [state, setState] = useState<SubmitState>("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    setMessage("");

    const form = new FormData(event.currentTarget);
    const payload = {
      operatorName: String(form.get("operatorName") || "").trim(),
      contactEmail: String(form.get("contactEmail") || "").trim(),
      endpoint: String(form.get("endpoint") || "").trim(),
      region: String(form.get("region") || "").trim(),
      gpu: String(form.get("gpu") || "").trim(),
      models: String(form.get("models") || "").trim(),
      notes: String(form.get("notes") || "").trim(),
      submittedAt: new Date().toISOString(),
      source: "marketing-site",
    };

    try {
      const response = await fetch("/api/register-seller", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setState("success");
      setMessage("已收到登记信息。我们会根据模型、区域、价格和协议能力安排联调。");
      event.currentTarget.reset();
    } catch {
      setState("error");
      setMessage("提交接口暂时不可用。请把同样的信息发送到 sellers@tokenbuddy.ai。");
    }
  };

  return (
    <section className="bg-[var(--color-page)] py-10 md:py-14 xl:py-16">
      <div className="page-container grid gap-6 xl:grid-cols-[0.42fr_1fr] xl:items-start">
        <aside className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] p-5 shadow-[0_12px_36px_rgba(60,41,112,0.07)] md:p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
            <Server className="h-4.5 w-4.5" aria-hidden="true" />
          </div>
          <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-primary-deep)]">
            Seller network
          </p>
          <h1 className="mt-2 text-[32px] font-bold leading-9 tracking-[0] text-[var(--color-ink-strong)] md:text-[40px] md:leading-[46px]">
            加入 TokenBuddy 小微算力网络
          </h1>
          <p className="mt-4 text-[15px] leading-7 text-[var(--color-text)] text-pretty">
            提交你的机房、模型服务或 GPU 节点信息。我们会优先联调协议清晰、价格透明、稳定性可验证的 seller。
          </p>
          <a
            href="mailto:sellers@tokenbuddy.ai"
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-white px-3 text-[13px] font-bold text-[var(--color-ink-mid)] transition-colors hover:bg-[var(--color-primary-soft)]"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            sellers@tokenbuddy.ai
          </a>
        </aside>

        <form
          onSubmit={onSubmit}
          className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] p-5 shadow-[0_12px_36px_rgba(60,41,112,0.07)] md:p-6"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="运营主体" name="operatorName" required placeholder="例如：上海某 GPU 工作室" />
            <Field label="联系邮箱" name="contactEmail" required type="email" placeholder="name@example.com" />
            <Field label="服务 endpoint" name="endpoint" required placeholder="https://example.com/v1" />
            <Field label="服务区域" name="region" required placeholder="sin / hkg / nrt / sfo" />
            <Field label="GPU / 算力描述" name="gpu" placeholder="例如：8x H100, 4x 4090" />
            <Field label="支持模型" name="models" required placeholder="gpt-4o, deepseek-v4, ..." />
          </div>

          <label className="mt-4 block">
            <span className="text-[12px] font-bold text-[var(--color-ink-mid)]">
              补充说明
            </span>
            <textarea
              name="notes"
              rows={5}
              className="mt-1.5 w-full rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] px-3 py-2.5 text-[14px] leading-6 text-[var(--color-ink-mid)] outline-none transition-colors placeholder:text-[var(--color-muted-soft)] focus:border-[var(--color-primary)]"
              placeholder="价格、并发、协议类型、结算方式、可联调时间等"
            />
          </label>

          {message ? (
            <p
              className={[
                "mt-4 rounded-[var(--radius-md)] border px-3 py-2 text-[13px] font-bold",
                state === "success"
                  ? "border-[var(--color-success-border)] bg-[var(--color-success-soft)] text-[var(--color-success-strong)]"
                  : "border-[var(--color-warning-border)] bg-[var(--color-warning-soft)] text-[var(--color-warning-strong)]",
              ].join(" ")}
            >
              {message}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={state === "submitting"}
            className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-primary)] px-4 text-[13.5px] font-bold text-white shadow-[0_10px_24px_rgba(124,61,240,0.14)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {state === "submitting" ? "提交中" : "提交接入申请"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  required = false,
  type = "text",
  placeholder,
}: {
  label: string;
  name: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-[12px] font-bold text-[var(--color-ink-mid)]">
        {label}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        className="mt-1.5 h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] px-3 text-[14px] text-[var(--color-ink-mid)] outline-none transition-colors placeholder:text-[var(--color-muted-soft)] focus:border-[var(--color-primary)]"
        placeholder={placeholder}
      />
    </label>
  );
}
